/**
 * Aviso que ve el usuario la PRIMERA vez que entra a la web después de registrarse
 * (épica SDS-31, ticket #368).
 *
 * Existe para que no se quede esperando en la pantalla equivocada: los primeros pasos
 * siguen por WhatsApp. La marca de "ya lo vio" es del usuario y vive en el servidor
 * (`aviso_bienvenida_pendiente`), así que no reaparece en otro dispositivo ni al recargar.
 */

import { useRouter } from 'next/router';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  SvgIcon,
  Typography,
  useTheme,
} from '@mui/material';
import ChatBubbleLeftRightIcon from '@heroicons/react/24/outline/ChatBubbleLeftRightIcon';
import { RUTA_SORBY_IA } from 'src/utils/sorbyIa/cuentaSorbyIa';
import { urlWhatsAppBot } from 'src/utils/sorbyIa/whatsappBot';

export const AvisoBienvenidaModal = ({ open, nombre, onClose, altaSola = false }) => {
  const theme = useTheme();
  const router = useRouter();

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        // Se cierra solo con el botón: un click al costado lo descartaría sin leerlo, y
        // este aviso se muestra una sola vez en la vida del usuario.
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onClose();
      }}
      disableEscapeKeyDown
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      <Box
        sx={{
          px: 3,
          pt: 4,
          pb: 3,
          textAlign: 'center',
          backgroundColor: theme.palette.success.lightest,
          borderBottom: `1px solid ${theme.palette.success.alpha12}`,
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            mx: 'auto',
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            backgroundColor: theme.palette.success.alpha12,
            color: theme.palette.success.dark,
          }}
        >
          <SvgIcon fontSize="medium">
            <ChatBubbleLeftRightIcon />
          </SvgIcon>
        </Box>
        <Typography variant="h6" sx={{ color: theme.palette.success.darkest }}>
          {nombre ? `Bienvenido, ${nombre}` : 'Bienvenido a Sorby'}
        </Typography>
      </Box>

      <DialogContent sx={{ px: 3, py: 3 }}>
        {altaSola ? (
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              La IA arma Sorby a tu medida a medida que lo usás.
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Podés seguir cargando por WhatsApp o por la web, como te quede más cómodo.
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Tus primeros pasos siguen por WhatsApp: desde ahí te vamos guiando para que
              cargues tu primer movimiento.
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Esta pantalla es para consultar y revisar. No hace falta que esperes nada acá.
            </Typography>
          </Stack>
        )}
      </DialogContent>

      {altaSola ? (
        <DialogActions sx={{ px: 3, pb: 3, pt: 0, flexDirection: 'column', gap: 1, '& > :not(style) + :not(style)': { ml: 0 } }}>
          <Button
            fullWidth
            variant="contained"
            sx={{ minHeight: 44 }}
            onClick={() => {
              onClose();
              router.push(RUTA_SORBY_IA);
            }}
          >
            Ver mi obra
          </Button>
          <Button
            fullWidth
            variant="outlined"
            sx={{ minHeight: 44 }}
            component="a"
            href={urlWhatsAppBot('Hola')}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClose}
          >
            Seguir por WhatsApp
          </Button>
        </DialogActions>
      ) : (
        <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
          <Button fullWidth variant="contained" onClick={onClose}>
            Entendido
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};
